import React, { createContext, useContext, useEffect, useState } from 'react';

type ThemePreference = 'light' | 'dark' | 'system';
type ResolvedTheme = 'light' | 'dark';

interface ThemeContextType {
    preference: ThemePreference;
    resolved: ResolvedTheme;
    setPreference: (theme: ThemePreference) => void;
}

const ThemeContext = createContext<ThemeContextType>({
    preference: 'system',
    resolved: 'light',
    setPreference: () => {},
});

const STORAGE_KEY = 'askhr-theme-preference';

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [preference, setPreferenceState] = useState<ThemePreference>(
        () => (localStorage.getItem(STORAGE_KEY) as ThemePreference) || 'system'
    );
    const [systemTheme, setSystemTheme] = useState<ResolvedTheme>(
        () => window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
    );

    useEffect(() => {
        const mq = window.matchMedia('(prefers-color-scheme: dark)');
        const handler = (e: MediaQueryListEvent) => setSystemTheme(e.matches ? 'dark' : 'light');
        mq.addEventListener('change', handler);
        return () => mq.removeEventListener('change', handler);
    }, []);

    const setPreference = (theme: ThemePreference) => {
        setPreferenceState(theme);
        localStorage.setItem(STORAGE_KEY, theme);
    };

    const resolved: ResolvedTheme = preference === 'system' ? systemTheme : preference;

    return (
        <ThemeContext.Provider value={{ preference, resolved, setPreference }}>
            {children}
        </ThemeContext.Provider>
    );
};

export const useAppTheme = () => useContext(ThemeContext);
